import { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosClient, apiClient } from '../../auth-lib';
import { Button, Input, Select, StatusPill, Table, EmptyState, useToast } from '../../design-system';
import './AuditLogExplorer.css';

// ─── The audit trail, one implementation, two audiences ─────────────────────
//
// Rendered by BOTH `/admin/audit-logs` (the platform console, every firm) and
// `/org-admin/audit-logs` (one firm's own trail). Shared rather than copied for
// the reason this codebase keeps paying for elsewhere: two screens showing the
// same rows drift, and an audit log that reads differently depending on where
// you opened it is worse than useless.
//
// `scope` changes what is asked for and what is shown, nothing else — the
// SERVER decides what a caller may actually see (auditLogController in both
// services scopes to the caller's own org unless they are a super admin). This
// prop is presentation: it must never be the thing keeping firms apart.

const ACTION_VARIANTS = {
  created: 'success',
  create: 'success',
  updated: 'info',
  update: 'info',
  status_changed: 'info',
  custody_changed: 'info',
  approved: 'success',
  restored: 'success',
  restore: 'success',
  reopened: 'info',
  rejected: 'danger',
  deleted: 'danger',
  delete: 'danger',
  revealed: 'warning', // a plaintext secret read — the highest-signal event
  reveal: 'warning',
  autofill: 'info',
  denied: 'danger',
  login: 'success',
  logout: 'neutral',
  login_failed: 'danger',
};

const OUTCOME_VARIANTS = { success: 'success', denied: 'danger', failed: 'danger' };

// Windows are day-granular and computed at fetch time, matching every other
// date window in this codebase.
const DAY_MS = 86400000;
const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'Everything' },
];

// How many rows on screen at once.
//
// ⚠️ This pages what has ALREADY BEEN FETCHED, not the trail on the server, and
// that is forced by the architecture rather than being a shortcut. Rows come
// from TWO services, are merged and de-duplicated here, and every filter except
// the date range is applied client-side — so a server-side page number would
// have to be agreed between two independently-sorted result sets before any of
// that ran. The date range is still the way to reach further back, and the
// truncation notice says so when a fetch actually hit its ceiling.
const PAGE_SIZES = [100, 200];

export function AuditLogExplorer({ scope = 'firm' }) {
  const isPlatform = scope === 'platform';

  const [logs, setLogs] = useState([]);
  const [orgNames, setOrgNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [outcome, setOutcome] = useState('all');
  const [range, setRange] = useState('30');
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const toast = useToast();

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      if (range !== 'all') params.from = new Date(Date.now() - Number(range) * DAY_MS).toISOString();

      // ⚠️ BOTH SERVICES, MERGED. auth-service owns the identity trail (users
      // created, roles changed, passwords and second factors reset,
      // subscriptions decided) and hrms owns everything the practice does.
      // Reading one would hand the firm half its own history — and which half
      // depends on a split that means nothing to the person reading it.
      //
      // Either call may legitimately fail (a firm without the permission on one
      // side, a service down), so neither is allowed to empty the other.
      const requests = [
        axiosClient.get('/audit-logs', { params }).catch(() => ({ data: [] })),
        apiClient.get('/api/audit-logs', { params }).catch(() => ({ data: [] })),
      ];
      // Org names resolve the Organization column, which only the platform view
      // has — a firm reading its own trail knows which firm it is.
      if (isPlatform) requests.push(axiosClient.get('/organizations').catch(() => ({ data: [] })));

      const [authLogs, taskLogs, orgs] = await Promise.all(requests);

      if (orgs) {
        const names = {};
        orgs.data.forEach((o) => { names[o._id] = o.name; });
        setOrgNames(names);
      }

      // Keyed by _id: the two services share a database today, so the same row
      // can arrive twice.
      const merged = new Map();
      [...authLogs.data, ...taskLogs.data].forEach((log) => merged.set(log._id, log));
      setLogs(Array.from(merged.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      toast.danger('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
    // `toast` is stable from the provider; excluded so a re-render can't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, isPlatform]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return logs.filter((log) => {
      if (entityType !== 'all' && log.entityType !== entityType) return false;
      if (outcome !== 'all' && (log.outcome || 'success') !== outcome) return false;
      if (term) {
        const hay = `${log.summary || ''} ${log.actorName || ''} ${log.actorRole || ''} ${log.requestId || ''} ${log.ipAddress || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [logs, search, entityType, outcome]);

  // Any change to WHAT is being listed invalidates the page number — page 4 of
  // the old result set describes nothing in the new one. Keyed on the filters
  // rather than on filteredLogs itself, so a refetch that returns the same rows
  // doesn't yank the reader back to the top.
  useEffect(() => {
    setPage(1);
  }, [search, entityType, outcome, range, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  // Clamped rather than stored: deleting or filtering rows away can strand the
  // reader past the end, and a blank table with a "Next" button is a dead end.
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * pageSize;
  const pageLogs = useMemo(
    () => filteredLogs.slice(firstIndex, firstIndex + pageSize),
    [filteredLogs, firstIndex, pageSize]
  );

  const entityOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.entityType).filter(Boolean));
    return Array.from(set).sort();
  }, [logs]);

  // Denials are the security signal worth surfacing without a filter click —
  // "who was refused something" is the question this data answers best.
  const deniedCount = useMemo(
    () => logs.filter((l) => (l.outcome || 'success') !== 'success').length,
    [logs]
  );

  const columns = [
    {
      key: 'createdAt',
      header: 'When',
      render: (l) => (
        <span className="audit-when" title={new Date(l.createdAt).toString()}>
          {new Date(l.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'actorName',
      header: 'Actor',
      render: (l) => (
        <div className="audit-actor">
          <span className="audit-actor-name">{l.actorName || '—'}</span>
          {l.actorRole && <span className="audit-actor-role">{l.actorRole}</span>}
        </div>
      ),
    },
    ...(isPlatform
      ? [{
          key: 'org',
          header: 'Organization',
          render: (l) => (l.organizationId
            ? (orgNames[l.organizationId] || '—')
            : <span className="audit-platform">Platform</span>),
        }]
      : []),
    { key: 'entityType', header: 'Entity', render: (l) => l.entityType },
    {
      key: 'action',
      header: 'Action',
      render: (l) => (
        <StatusPill variant={ACTION_VARIANTS[l.action] || 'neutral'} label={(l.action || '').replace(/_/g, ' ')} />
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (l) => <StatusPill variant={OUTCOME_VARIANTS[l.outcome] || 'neutral'} label={l.outcome || 'success'} />,
    },
    { key: 'summary', header: 'Summary', render: (l) => l.summary },
    {
      key: 'trace',
      header: 'Trace',
      render: (l) =>
        l.requestId ? (
          <code
            className="audit-trace"
            title={`request: ${l.requestId}\nip: ${l.ipAddress || '—'}\ndevice: ${l.deviceId || '—'}\nagent: ${l.userAgent || '—'}`}
          >
            {String(l.requestId).slice(0, 8)}
          </code>
        ) : (
          <span className="audit-trace-none">—</span>
        ),
    },
  ];

  return (
    <div className="audit-logs-page">
      <div className="audit-logs-toolbar">
        <Input
          className="audit-logs-search"
          placeholder="🔎 Search actor, summary, role, IP, trace id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* The only filter applied SERVER-side — everything else narrows what
            has already been fetched, so changing it must refetch. */}
        <Select value={range} onChange={(e) => setRange(e.target.value)} className="audit-logs-filter">
          {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
        <Select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="audit-logs-filter">
          <option value="all">All Entities</option>
          {entityOptions.map((et) => <option key={et} value={et}>{et}</option>)}
        </Select>
        <Select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="audit-logs-filter">
          <option value="all">All Outcomes</option>
          <option value="success">Success</option>
          <option value="denied">Denied</option>
          <option value="failed">Failed</option>
        </Select>
        <Select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="audit-logs-filter"
          aria-label="Entries per page"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </Select>
        <Button variant="secondary" onClick={fetchLogs} disabled={loading}>Refresh</Button>
      </div>

      {/* ⚠️ The cap is the SERVER'S 500 per service, so a busy firm's window can
          be truncated. Said out loud rather than left to look like the whole
          story — an audit trail that quietly stops short is the one kind of
          incompleteness that actually matters. */}
      <div className="audit-logs-meta">
        <span>
          {/* Both numbers, always: which slice is on screen AND how big the
              matching set is. "Showing 101–200" alone leaves the reader unable
              to tell whether they are near the end. */}
          {filteredLogs.length === 0
            ? 'No entries'
            : `Showing ${firstIndex + 1}–${Math.min(firstIndex + pageSize, filteredLogs.length)}`
              + ` of ${filteredLogs.length}`
              + (filteredLogs.length === logs.length ? ' entries' : ` matching (${logs.length} fetched)`)}
        </span>
        {deniedCount > 0 && (
          <button
            type="button"
            className="audit-logs-denied"
            onClick={() => setOutcome(outcome === 'denied' ? 'all' : 'denied')}
          >
            {deniedCount} denied or failed
          </button>
        )}
        {/* 1000 = both services returned their 500-row cap, i.e. the window was
            truncated. Now that there is a pager this matters MORE, not less: a
            reader who reaches the last page would otherwise take it for the end
            of the trail. Paging moves through what was fetched; only a narrower
            window reaches further back. */}
        {logs.length >= 1000 && (
          <span className="audit-logs-truncated">
            Only the most recent entries were fetched — the last page is not the end of the trail.
            Narrow the date range to see further back.
          </span>
        )}
      </div>

      {!loading && filteredLogs.length === 0 ? (
        <EmptyState
          icon="📜"
          title="No audit log entries found"
          description="Try a wider date range, a different filter, or another search term."
        />
      ) : (
        <>
          <Table columns={columns} data={pageLogs} loading={loading} />
          {/* Only when there is more than one page — a pager under a 40-row
              table is furniture that says nothing. */}
          {totalPages > 1 && (
            <div className="audit-logs-pager">
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="audit-logs-pageno">Page {currentPage} of {totalPages}</span>
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AuditLogExplorer;
